export const newsItems = [
  {
    id: 1,
    title: 'New Scholarship Opportunities',
    excerpt: 'Discover the latest scholarship programs for international students...',
    date: '2024-01-15',
    fullContent: `We are excited to announce several new scholarship opportunities for international students planning to study abroad in 2024. These scholarships are designed to support talented students from around the world in pursuing their educational dreams.

## Available Scholarships:

### 1. International Excellence Scholarship
- **Amount**: Up to $15,000 per year
- **Eligibility**: High academic achievement (GPA 3.5+)
- **Application Deadline**: March 15, 2024

### 2. Diversity and Inclusion Scholarship
- **Amount**: Up to $10,000 per year
- **Eligibility**: Students from underrepresented regions
- **Application Deadline**: February 28, 2024

### 3. STEM Innovation Scholarship
- **Amount**: Up to $20,000 per year
- **Eligibility**: Students pursuing STEM degrees
- **Application Deadline**: April 1, 2024

## How to Apply:
1. Complete the online application form
2. Submit academic transcripts
3. Write a personal statement (500 words)
4. Provide two recommendation letters

## Important Notes:
- All scholarships are renewable based on academic performance
- Students can apply for multiple scholarships
- Early applications are encouraged

For more information and to apply, please visit our scholarship portal or contact our admissions team.`,
    author: 'Edugration Team',
    category: 'Scholarships',
    readTime: '5 min read'
  },
  {
    id: 2,
    title: 'University Application Deadlines',
    excerpt: 'Important dates and deadlines for upcoming university applications...',
    date: '2024-01-12',
    fullContent: `Stay ahead of your study abroad journey with our comprehensive guide to university application deadlines for 2024. Missing these critical dates could delay your educational plans by a full year.

## Key Application Deadlines:

### Fall 2024 Intake (September Start)
- **Early Decision**: November 1, 2023 (Past)
- **Regular Decision**: January 15, 2024 (Past)
- **Late Applications**: March 1, 2024

### Spring 2025 Intake (January Start)
- **Priority Deadline**: August 15, 2024
- **Final Deadline**: October 15, 2024

### Summer 2025 Intake (May Start)
- **Application Opens**: December 1, 2024
- **Priority Deadline**: February 15, 2025

## Country-Specific Deadlines:

### United States
- **UCAS Equivalent**: Varies by university
- **Common App**: January 1 - March 1, 2024

### Canada
- **Ontario Universities**: January 15, 2024
- **Other Provinces**: March 1, 2024

### United Kingdom
- **UCAS Early**: October 15, 2023 (Past)
- **UCAS Regular**: January 31, 2024 (Past)
- **UCAS Late**: June 30, 2024

### Australia
- **Semester 1**: December 1, 2023 (Past)
- **Semester 2**: May 31, 2024

## Tips for Success:
- Start your applications 6 months before deadlines
- Prepare all required documents in advance
- Consider backup options
- Don't wait until the last minute

## Required Documents:
- Academic transcripts
- English proficiency test scores (IELTS/TOEFL)
- Statement of purpose
- Letters of recommendation
- Financial documentation
- Passport copy

Need help with your applications? Our expert counselors are here to guide you through the entire process.`,
    author: 'Admissions Team',
    category: 'Applications',
    readTime: '7 min read'
  },
  {
    id: 3,
    title: 'Visa Process Updates',
    excerpt: 'Latest updates on student visa application processes...',
    date: '2024-01-10',
    fullContent: `Important updates to student visa processes across major study destinations. These changes will affect students planning to study abroad in 2024 and beyond.

## Recent Visa Process Changes:

### United States (F-1 Visa)
- **New Processing Times**: 60-90 days (increased from 30-45 days)
- **Interview Waiver**: Extended for certain countries
- **SEVIS Fee**: Increased to $350 (effective January 1, 2024)

### United Kingdom (Student Visa)
- **Processing Time**: 3-8 weeks for standard applications
- **Priority Service**: Available for additional fee (£500)
- **Super Priority**: 24-hour processing (£800)

### Canada (Study Permit)
- **Processing Time**: 4-20 weeks depending on country
- **Biometrics**: Required for all applicants
- **SDS Program**: Streamlined process for certain countries

### Australia (Student Visa)
- **Processing Time**: 1-4 months
- **GTE Requirements**: Stricter genuine temporary entrant criteria
- **Health Insurance**: Mandatory OSHC coverage

## General Visa Requirements:

### Essential Documents:
- Valid passport (6+ months validity)
- Letter of acceptance from institution
- Financial proof (bank statements)
- Academic transcripts
- English proficiency test results
- Health insurance (where required)
- Biometric data (where required)

### Financial Requirements:
- **US**: Proof of funds for first year of study
- **UK**: £1,334 per month (outside London) / £1,023 (London)
- **Canada**: $10,000 CAD + tuition + living expenses
- **Australia**: $21,041 AUD per year

## Tips for Successful Visa Applications:

1. **Start Early**: Begin process 3-6 months before travel
2. **Accurate Information**: Double-check all details
3. **Financial Documentation**: Ensure sufficient funds are clearly shown
4. **Interview Preparation**: Practice common questions
5. **Document Organization**: Keep everything well-organized

## Common Rejection Reasons:
- Insufficient financial documentation
- Unclear study plans
- Weak ties to home country
- Incomplete application
- False information

## Emergency Processing:
- Available in certain countries for urgent cases
- Additional fees apply
- Limited availability

Our visa specialists can help you navigate these complex processes and maximize your chances of approval.`,
    author: 'Visa Department',
    category: 'Visa',
    readTime: '8 min read'
  },
  {
    id: 4,
    title: 'Study Abroad Success Stories',
    excerpt: 'Inspiring stories from our successful students...',
    date: '2024-01-08',
    fullContent: `Read about the incredible journeys of our students who have successfully pursued their dreams of studying abroad. These inspiring stories showcase the transformative power of international education.

## Featured Success Stories:

### Maria Santos - Computer Science at MIT
*From Brazil to Massachusetts Institute of Technology*

"I never thought I could study at one of the world's top universities. Edugration helped me understand the application process and secure a full scholarship. Now I'm working on AI research that could change the world."

**Key Achievements:**
- Full scholarship worth $200,000
- Research internship at Google
- Published 2 papers in top-tier conferences

### Ahmed Hassan - Medicine at University of Edinburgh
*From Egypt to Scotland*

"Studying medicine in Edinburgh was challenging but incredibly rewarding. The diverse student body and world-class faculty made every day a learning experience."

**Key Achievements:**
- Graduated with distinction
- Completed residency in cardiology
- Now practicing in London

### Priya Sharma - Business Administration at Wharton
*From India to University of Pennsylvania*

"The MBA program at Wharton opened doors I never knew existed. The network and education have been invaluable for my career."

**Key Achievements:**
- Top 10% of graduating class
- Internship at McKinsey & Company
- Now VP at major tech company

### James Okonkwo - Engineering at University of Toronto
*From Nigeria to Canada*

"Canada's welcoming environment and excellent education system made my transition smooth. I'm now contributing to sustainable energy solutions."

**Key Achievements:**
- Co-founded clean energy startup
- Won national engineering competition
- Secured $2M in funding

## Common Success Factors:

### Academic Excellence
- Strong academic performance
- Relevant extracurricular activities
- Clear career goals

### Personal Qualities
- Adaptability and resilience
- Cultural sensitivity
- Strong communication skills

### Preparation
- Thorough research about destination
- Financial planning
- Language preparation

## Support Systems:

### Family Support
- Emotional encouragement
- Financial assistance
- Cultural guidance

### Institutional Support
- Academic advisors
- Career services
- Student organizations

### Peer Networks
- International student communities
- Alumni networks
- Professional associations

## Challenges Overcome:

### Cultural Adjustment
- Language barriers
- Different academic systems
- Social integration

### Financial Management
- Budgeting in foreign currency
- Managing expenses
- Finding part-time work

### Academic Pressure
- Higher expectations
- Different teaching styles
- Research requirements

## Life After Graduation:

### Career Opportunities
- Global job market access
- Higher earning potential
- International experience valued

### Personal Growth
- Enhanced cultural awareness
- Improved language skills
- Expanded worldview

### Network Building
- International connections
- Professional relationships
- Lifelong friendships

## Advice for Future Students:

1. **Start Early**: Begin planning 12-18 months in advance
2. **Research Thoroughly**: Understand your destination country
3. **Stay Focused**: Maintain academic performance
4. **Be Open-Minded**: Embrace new experiences
5. **Build Networks**: Connect with fellow students

These success stories prove that with determination, proper guidance, and hard work, your study abroad dreams can become reality.`,
    author: 'Student Affairs Team',
    category: 'Success Stories',
    readTime: '10 min read'
  },
  {
    id: 5,
    title: 'Education Fair Announcement',
    excerpt: 'Join us for the upcoming international education fair...',
    date: '2024-01-05',
    fullContent: `We're excited to announce our biggest international education fair of the year! Join us for an incredible opportunity to connect with universities from around the world and take the first step toward your global education journey.

## Event Details:

### Date and Time:
- **Main Event**: March 15-16, 2024
- **Hours**: 9:00 AM - 6:00 PM (both days)
- **Location**: International Convention Center, Downtown

### Participating Countries:
- **North America**: USA, Canada
- **Europe**: UK, Germany, Netherlands, Sweden, Denmark, Finland
- **Asia-Pacific**: Australia, New Zealand, Singapore, Japan
- **Others**: Ireland, Malta, Cyprus

## What to Expect:

### University Representatives:
- 200+ universities from 15+ countries
- Direct access to admissions officers
- On-the-spot application reviews
- Scholarship information sessions

### Workshops and Seminars:
- **Study Abroad Planning**: Step-by-step guidance
- **Visa Process**: Latest updates and requirements
- **Scholarship Opportunities**: How to secure funding
- **Career Prospects**: Job opportunities after graduation
- **Language Preparation**: IELTS/TOEFL strategies

### Interactive Sessions:
- One-on-one counseling
- Application assistance
- Document review
- Interview preparation

## Featured Universities:

### Top-Tier Institutions:
- Harvard University (USA)
- University of Oxford (UK)
- University of Toronto (Canada)
- University of Melbourne (Australia)
- Technical University of Munich (Germany)

### Specialized Programs:
- Engineering and Technology
- Medicine and Healthcare
- Business and Management
- Arts and Humanities
- Sciences and Research

## Exclusive Benefits:

### For Attendees:
- **Free Application Fees**: Waived for select universities
- **Priority Processing**: Faster application review
- **Scholarship Opportunities**: Exclusive funding options
- **Mentorship Program**: Connect with alumni

### Special Offers:
- 50% discount on application services
- Free document translation
- Complimentary visa consultation
- Study abroad guidebook

## How to Prepare:

### Before the Event:
1. **Research Universities**: Create a wish list
2. **Prepare Documents**: Bring copies of transcripts
3. **Write Questions**: List your specific queries
4. **Dress Professionally**: Make a good impression

### What to Bring:
- Academic transcripts (certified copies)
- English proficiency test scores
- Passport copy
- CV/Resume
- Statement of purpose draft
- Recommendation letters (if available)

## Registration:

### Online Registration:
- **Early Bird**: Free (until February 15)
- **Regular**: $25 (after February 15)
- **On-site**: $35

### Group Discounts:
- 5+ students: 20% off
- 10+ students: 30% off
- School groups: Special rates

## Virtual Component:
For those unable to attend in person, we're offering:
- Live streaming of key sessions
- Virtual university booths
- Online counseling appointments
- Digital resource library

## Success Stories:
Last year's fair resulted in:
- 2,500+ successful applications
- $5M+ in scholarships awarded
- 95% visa approval rate
- 100+ universities represented

## Contact Information:
- **Phone**: +1-800-EDUGRATION
- **Email**: fair@edugration.com
- **Website**: www.edugration.com/fair
- **Social Media**: @EdugrationFair

Don't miss this incredible opportunity to jumpstart your international education journey. Register now and secure your spot at the premier education fair of the year!`,
    author: 'Events Team',
    category: 'Events',
    readTime: '6 min read'
  },
  {
    id: 6,
    title: 'IELTS vs TOEFL: Which Test to Choose?',
    excerpt: 'A comprehensive comparison of English proficiency tests for international students...',
    date: '2024-01-03',
    fullContent: `Choosing the right English proficiency test is crucial for your study abroad application. This comprehensive guide will help you decide between IELTS and TOEFL based on your specific needs and goals.

## Test Overview:

### IELTS (International English Language Testing System)
- **Format**: Paper-based and Computer-based
- **Duration**: 2 hours 45 minutes
- **Sections**: Listening, Reading, Writing, Speaking
- **Scoring**: 0-9 band scale
- **Cost**: $245-$255 USD

### TOEFL (Test of English as a Foreign Language)
- **Format**: Internet-based (iBT) and Paper-based
- **Duration**: 3 hours (iBT)
- **Sections**: Reading, Listening, Speaking, Writing
- **Scoring**: 0-120 points
- **Cost**: $185-$265 USD

## Detailed Comparison:

### Test Structure:

#### IELTS:
- **Listening**: 40 questions, 30 minutes + 10 minutes transfer time
- **Reading**: 40 questions, 60 minutes
- **Writing**: 2 tasks, 60 minutes
- **Speaking**: Face-to-face interview, 11-14 minutes

#### TOEFL:
- **Reading**: 30-40 questions, 54-72 minutes
- **Listening**: 28-39 questions, 41-57 minutes
- **Speaking**: 4 tasks, 17 minutes
- **Writing**: 2 tasks, 50 minutes

### Scoring Systems:

#### IELTS Band Scores:
- **9**: Expert user
- **8**: Very good user
- **7**: Good user
- **6**: Competent user
- **5**: Modest user
- **4**: Limited user
- **3**: Extremely limited user
- **2**: Intermittent user
- **1**: Non-user
- **0**: Did not attempt

#### TOEFL Score Ranges:
- **Reading**: 0-30
- **Listening**: 0-30
- **Speaking**: 0-30
- **Writing**: 0-30
- **Total**: 0-120

## University Requirements:

### Popular Score Requirements:

#### Top US Universities:
- **Harvard**: IELTS 7.0 / TOEFL 100
- **MIT**: IELTS 7.0 / TOEFL 90
- **Stanford**: IELTS 7.0 / TOEFL 100

#### Top UK Universities:
- **Oxford**: IELTS 7.0 / TOEFL 100
- **Cambridge**: IELTS 7.5 / TOEFL 110
- **Imperial**: IELTS 6.5 / TOEFL 92

#### Top Canadian Universities:
- **UofT**: IELTS 6.5 / TOEFL 100
- **UBC**: IELTS 6.5 / TOEFL 90
- **McGill**: IELTS 6.5 / TOEFL 86

## Which Test to Choose:

### Choose IELTS If:
- You prefer face-to-face speaking test
- You're applying to UK universities
- You want paper-based option
- You prefer British English
- You need faster results (5-7 days)

### Choose TOEFL If:
- You're applying to US universities
- You prefer computer-based format
- You're comfortable with American English
- You want integrated tasks
- You need detailed score breakdown

## Preparation Strategies:

### IELTS Preparation:
- Practice with official Cambridge materials
- Focus on task types in each section
- Improve time management
- Practice speaking with native speakers
- Work on British English pronunciation

### TOEFL Preparation:
- Use official ETS practice tests
- Practice integrated tasks
- Improve typing speed
- Focus on note-taking skills
- Practice speaking into microphone

## Test Day Tips:

### General Tips:
- Arrive early (30 minutes before)
- Bring valid ID and confirmation
- Stay calm and focused
- Read instructions carefully
- Manage your time wisely

### IELTS Specific:
- Use the 10-minute transfer time effectively
- Practice handwriting if taking paper test
- Make eye contact during speaking test
- Use varied vocabulary and grammar

### TOEFL Specific:
- Practice typing essays
- Take notes during listening
- Speak clearly into microphone
- Use the full time for each section

## Score Conversion:
While not officially equivalent, approximate conversions:
- IELTS 9.0 ≈ TOEFL 118-120
- IELTS 8.5 ≈ TOEFL 115-117
- IELTS 8.0 ≈ TOEFL 110-114
- IELTS 7.5 ≈ TOEFL 102-109
- IELTS 7.0 ≈ TOEFL 94-101
- IELTS 6.5 ≈ TOEFL 79-93
- IELTS 6.0 ≈ TOEFL 60-78

## Retake Policies:

### IELTS:
- Can retake anytime
- No limit on attempts
- Must wait for results before retaking
- Can retake individual sections (in some countries)

### TOEFL:
- Can retake after 3 days
- No limit on attempts
- Can retake up to 4 times per year
- Must wait for results before retaking

## Cost and Availability:

### IELTS:
- Available 48 times per year
- 1,600+ test centers worldwide
- Results in 5-7 days (computer) / 13 days (paper)

### TOEFL:
- Available 50+ times per year
- 4,500+ test centers worldwide
- Results in 6-10 days

## Special Accommodations:
Both tests offer accommodations for:
- Learning disabilities
- Physical disabilities
- Medical conditions
- Religious observances

## Final Recommendation:
- **US-focused students**: TOEFL
- **UK-focused students**: IELTS
- **Mixed applications**: IELTS (more widely accepted)
- **Prefer face-to-face**: IELTS
- **Prefer computer-based**: TOEFL

Remember, both tests are designed to assess your English proficiency for academic purposes. Choose based on your target universities and personal preferences.`,
    author: 'Testing Center',
    category: 'Testing',
    readTime: '9 min read'
  }
];
